// Iter 83.5 / Phase E4 Sprint 5 — Manager Brief 1-pager Print.
// Cross-module operational snapshot for an employee: identity, tenure,
// attendance, leave balances, last 3 payslips, open loans, assets,
// open exit/FNF. Print-ready A4.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const fmtMoney = (n) =>
    `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

export default function ManagerBriefPrint() {
    const { employeeId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data: d } = await api.get(`/manager-brief/${employeeId}`);
                setData(d);
            } catch (err) {
                setError(formatApiError(err, "Failed to load manager brief"));
                toast.error(formatApiError(err, "Load failed"));
            }
        })();
    }, [employeeId]);

    if (error) return <div className="p-12 text-center text-rose-600" data-testid="manager-brief-error">{error}</div>;
    if (!data) return <div className="p-12 text-center text-slate-400">Loading…</div>;

    const {
        employee, manager, tenure, window: win,
        attendance_summary: att, leave_balances: balances,
        last_payslips: payslips, open_loans, open_assignments,
        open_exit, open_fnf, company, generated_at,
    } = data;

    return (
        <div className="bg-white min-h-screen p-10 font-sans text-[12px] leading-snug" data-testid="manager-brief-print">
            <style>{`@media print { @page { size: A4; margin: 10mm; } body { -webkit-print-color-adjust: exact; } }`}</style>

            {/* Header */}
            <header className="border-b-4 border-slate-900 pb-3 mb-4 flex justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{company.legal_name}</div>
                    <h1 className="text-2xl font-bold mt-0.5">Manager Brief</h1>
                    <div className="text-[11px] text-slate-600">Operational 1-pager · {win.from} to {win.to}</div>
                </div>
                <div className="text-right text-[11px]">
                    <div className="text-[9px] uppercase tracking-widest text-slate-500">Generated</div>
                    <div className="font-mono">{(generated_at || "").slice(0, 19).replace("T", " ")}</div>
                </div>
            </header>

            {/* Identity + Manager */}
            <section className="grid grid-cols-3 gap-x-6 gap-y-2 border-b pb-3 mb-3">
                <Pair label="Employee" value={`${employee.full_name} · ${employee.employee_code}`} />
                <Pair label="Designation" value={`${employee.designation || "—"} · ${employee.department || "—"}`} />
                <Pair label="Status" value={<span className={`px-1.5 py-0.5 rounded text-[10px] ${employee.status === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{employee.status}</span>} />
                <Pair label="Reporting Manager" value={manager ? `${manager.full_name} · ${manager.designation || ""}` : "—"} />
                <Pair label="Date of Joining" value={employee.date_of_joining || "—"} />
                <Pair label="Tenure" value={`${tenure.years}y ${tenure.months}m ${tenure.days}d`} />
                <Pair label="Email" value={employee.work_email || employee.personal_email || "—"} />
                <Pair label="Mobile" value={employee.personal_mobile || "—"} />
                <Pair label="Location" value={employee.location || "—"} />
            </section>

            {/* Attendance + Leave */}
            <section className="grid grid-cols-2 gap-4 mb-3">
                <div>
                    <H>Attendance · Last 90 days</H>
                    <div className="grid grid-cols-3 gap-1 text-center mt-1">
                        <Mini label="Present" value={att.days_present} tone="bg-emerald-50" />
                        <Mini label="Leave" value={att.days_leave} tone="bg-amber-50" />
                        <Mini label="Shortfall" value={att.days_shortfall} tone="bg-rose-50" />
                        <Mini label="WeekOff" value={att.days_weekoff} tone="bg-slate-50" />
                        <Mini label="Holiday" value={att.days_holiday} tone="bg-blue-50" />
                        <Mini label="Tracked" value={att.days_with_events} tone="bg-slate-100" />
                    </div>
                </div>
                <div>
                    <H>Leave Balances</H>
                    {balances.length === 0 ? (
                        <div className="text-slate-400 text-[11px] mt-1">No balances tracked</div>
                    ) : (
                        <table className="w-full text-[11px] mt-1">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="text-left px-1 py-0.5">Type</th>
                                    <th className="text-right px-1 py-0.5">Opening</th>
                                    <th className="text-right px-1 py-0.5">Accrued</th>
                                    <th className="text-right px-1 py-0.5">Consumed</th>
                                    <th className="text-right px-1 py-0.5">Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {balances.map((b, i) => (
                                    <tr key={i} className="border-b">
                                        <td className="px-1 py-0.5">{b.leave_type_code || b.leave_type_name}</td>
                                        <td className="px-1 py-0.5 text-right font-mono">{fmtNum(b.opening_balance)}</td>
                                        <td className="px-1 py-0.5 text-right font-mono">{fmtNum(b.accrued)}</td>
                                        <td className="px-1 py-0.5 text-right font-mono">{fmtNum(b.consumed)}</td>
                                        <td className="px-1 py-0.5 text-right font-mono font-semibold">{fmtNum(b.balance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>

            {/* Payslips */}
            <section className="mb-3">
                <H>Last 3 Payslips</H>
                {payslips.length === 0 ? (
                    <div className="text-slate-400 text-[11px] mt-1">No payslips yet</div>
                ) : (
                    <table className="w-full text-[11px] mt-1">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="text-left px-1 py-0.5">Period</th>
                                <th className="text-right px-1 py-0.5">Gross</th>
                                <th className="text-right px-1 py-0.5">Deductions</th>
                                <th className="text-right px-1 py-0.5">Net Pay</th>
                                <th className="text-left px-1 py-0.5">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payslips.map((p) => (
                                <tr key={p.id} className="border-b">
                                    <td className="px-1 py-0.5">{p.period_label || `${p.period_start} → ${p.period_end}`}</td>
                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(p.gross_earnings)}</td>
                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(p.total_deductions)}</td>
                                    <td className="px-1 py-0.5 text-right font-mono font-semibold">{fmtMoney(p.net_pay)}</td>
                                    <td className="px-1 py-0.5">{p.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* Loans + Assets */}
            <section className="grid grid-cols-2 gap-4 mb-3">
                <div>
                    <H>Open Loans / Advances</H>
                    {open_loans.length === 0 ? (
                        <div className="text-slate-400 text-[11px] mt-1">No open loans</div>
                    ) : (
                        <table className="w-full text-[11px] mt-1">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="text-left px-1 py-0.5">Policy / ID</th>
                                    <th className="text-right px-1 py-0.5">Principal</th>
                                    <th className="text-right px-1 py-0.5">Outstanding</th>
                                    <th className="text-right px-1 py-0.5">EMI</th>
                                    <th className="text-left px-1 py-0.5">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {open_loans.map((l) => (
                                    <tr key={l.id} className="border-b">
                                        <td className="px-1 py-0.5">
                                            <div className="font-mono text-[10px]">{l.id}</div>
                                            <div className="text-[9px] text-slate-500">{l.policy_code_snapshot || ""}</div>
                                        </td>
                                        <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(l.principal)}</td>
                                        <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(l.outstanding_principal)}</td>
                                        <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(l.emi_amount)}</td>
                                        <td className="px-1 py-0.5">{l.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div>
                    <H>Currently Assigned Assets</H>
                    {open_assignments.length === 0 ? (
                        <div className="text-slate-400 text-[11px] mt-1">No assets assigned</div>
                    ) : (
                        <table className="w-full text-[11px] mt-1">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="text-left px-1 py-0.5">Asset</th>
                                    <th className="text-left px-1 py-0.5">Category</th>
                                    <th className="text-right px-1 py-0.5">Dep. Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {open_assignments.map((a, i) => (
                                    <tr key={i} className="border-b">
                                        <td className="px-1 py-0.5 font-mono">{a.asset_tag || a.asset_id}</td>
                                        <td className="px-1 py-0.5">{a.category_name_snapshot || "—"}</td>
                                        <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(a.depreciated_value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>

            {/* Exit / FNF banner if applicable */}
            {(open_exit || open_fnf) && (
                <section className="border-t-2 border-rose-500 pt-2 mb-3">
                    <H>Exit / FNF Status</H>
                    <div className="bg-rose-50 border border-rose-300 rounded p-2 mt-1 text-[11px]">
                        {open_exit && (
                            <div>
                                <span className="font-semibold">Exit:</span> {open_exit.exit_type} · LWD {open_exit.last_working_day} · Status: <span className="font-semibold">{open_exit.status}</span>
                            </div>
                        )}
                        {open_fnf && (
                            <div className="mt-1">
                                <span className="font-semibold">FNF:</span> {open_fnf.exit_type} · LWD {open_fnf.lwd} · Net: {fmtMoney(open_fnf.net_settlement_amount)} · Status: <span className="font-semibold">{open_fnf.status}</span>
                                {open_fnf.is_negative && <span className="ml-2 text-rose-700">D23 NEGATIVE · Shortfall {fmtMoney(open_fnf.shortfall_amount)}</span>}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="border-t border-slate-300 pt-2 text-[10px] text-slate-500 flex justify-between">
                <div>This brief is auto-generated from REEDOS. For corrections, contact HR Operations.</div>
                <div className="font-mono">© {new Date().getFullYear()} {company.legal_name}</div>
            </footer>
        </div>
    );
}

function Pair({ label, value }) {
    return (
        <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="text-[12px]">{value || "—"}</div>
        </div>
    );
}

function H({ children }) {
    return <div className="text-[10px] font-bold uppercase tracking-widest text-slate-700 border-b border-slate-200 pb-0.5">{children}</div>;
}

function Mini({ label, value, tone }) {
    return (
        <div className={`rounded p-1.5 ${tone || "bg-slate-50"}`}>
            <div className="text-[8px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="text-lg font-bold">{value}</div>
        </div>
    );
}
